import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emptyData, mergeSettings, type Collection, type Data, type Settings } from '@biuro/shared';
import { api } from './api.ts';

// ---------- dane ----------
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: 30_000 });
}

export function useData() {
  const q = useQuery({ queryKey: ['data'], queryFn: api.data, staleTime: 10_000 });
  return { ...q, data: q.data ?? emptyData() };
}

export function useSettings(): Settings {
  const { data } = useData();
  return mergeSettings(data.settings.main);
}

type DocVars = { col: Collection; id: string; doc: unknown };

/** Zapis dokumentu z natychmiastową aktualizacją widoku (optimistic update). */
export function useSaveDoc() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ col, id, doc }: DocVars) => api.put(col, id, doc),
    onMutate: async ({ col, id, doc }) => {
      await qc.cancelQueries({ queryKey: ['data'] });
      const prev = qc.getQueryData<Data>(['data']);
      if (prev) qc.setQueryData<Data>(['data'], { ...prev, [col]: { ...prev[col], [id]: doc } });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['data'], ctx.prev);
      toast((e as Error).message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['data'] }),
  });
}

export function useDeleteDoc() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ col, id }: { col: Collection; id: string }) => api.del(col, id),
    onMutate: async ({ col, id }) => {
      await qc.cancelQueries({ queryKey: ['data'] });
      const prev = qc.getQueryData<Data>(['data']);
      if (prev) {
        const c = { ...prev[col] } as Record<string, unknown>;
        delete c[id];
        qc.setQueryData<Data>(['data'], { ...prev, [col]: c });
      }
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['data'], ctx.prev);
      toast((e as Error).message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['data'] }),
  });
}

/** Zapis fragmentu ustawień (scalany z bieżącymi). */
export function useSaveSettings() {
  const save = useSaveDoc();
  const { data } = useData();
  return useCallback(
    (patch: Partial<Settings>) => save.mutate({ col: 'settings', id: 'main', doc: { ...(data.settings.main || {}), ...patch } }),
    [save, data.settings.main],
  );
}

// ---------- nawigacja ----------
export const TABS = [
  { key: 'pulpit', label: 'Dziś', short: 'Dziś' },
  { key: 'terminy', label: 'Terminy', short: 'Terminy' },
  { key: 'faktury', label: 'Faktury zagraniczne', short: 'Faktury' },
  { key: 'miesiac', label: 'Zamknięcie miesiąca', short: 'Miesiąc' },
  { key: 'firma', label: 'Firma', short: 'Firma' },
] as const;
export type Tab = (typeof TABS)[number]['key'];

/** Stare adresy z poprzedniej wersji aplikacji. */
const ALIASES: Record<string, { tab: Tab; sub?: string }> = {
  uslugi: { tab: 'faktury' },
  dokumenty: { tab: 'miesiac' },
};
/** Podstrony z własnym adresem (np. /firma/rodo). */
const PATH_SUBS = new Set(['rodo']);

function fromPath(): { tab: Tab; sub: string | null } {
  const [a = '', b = ''] = location.pathname.replace(/^\/+/, '').split('/');
  const hit = TABS.find((t) => t.key === a)?.key as Tab | undefined;
  if (hit) return { tab: hit, sub: PATH_SUBS.has(b) ? b : null };
  if (ALIASES[a]) return { tab: ALIASES[a].tab, sub: ALIASES[a].sub ?? null };
  return { tab: 'pulpit', sub: null };
}

interface Nav {
  tab: Tab;
  go: (t: Tab, opts?: { sub?: string }) => void;
  sub: string | null;
}
const NavCtx = createContext<Nav>({ tab: 'pulpit', go: () => {}, sub: null });
export const useNav = () => useContext(NavCtx);

export function NavProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(fromPath);
  useEffect(() => {
    const on = () => setState(fromPath());
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  const go = useCallback((t: Tab, opts?: { sub?: string }) => {
    const sub = opts?.sub ?? null;
    const path = t === 'pulpit' ? '/' : `/${t}${sub && PATH_SUBS.has(sub) ? `/${sub}` : ''}`;
    history.pushState(null, '', path);
    setState({ tab: t, sub });
    window.scrollTo(0, 0);
  }, []);
  return <NavCtx.Provider value={{ tab: state.tab, go, sub: state.sub }}>{children}</NavCtx.Provider>;
}

// ---------- komunikaty ----------
const ToastCtx = createContext<(msg: string) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const t = useRef<number | undefined>(undefined);
  const show = useCallback((m: string) => {
    setMsg(m);
    window.clearTimeout(t.current);
    t.current = window.setTimeout(() => setMsg(null), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {msg && <div className="toast" role="status">{msg}</div>}
    </ToastCtx.Provider>
  );
}

/** Kopiowanie do schowka z komunikatem. */
export function useCopy() {
  const toast = useToast();
  return useCallback(
    (text: string) => {
      if (!text) return toast('Pole jest puste');
      navigator.clipboard?.writeText(text).then(() => toast('Skopiowano'), () => toast('Nie udało się skopiować'));
    },
    [toast],
  );
}

/** Pobranie pliku wygenerowanego w przeglądarce. */
export function downloadFile(name: string, data: BlobPart, type: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
